import { useEffect, useId, useRef } from 'react';
import { Lightbulb, X } from 'lucide-react';
import { capacityModel, dropsMetered } from '../../lib/capacityModel';

// Full symbology reference for the design map + KMZ export. The colours/shapes
// here mirror exactly what DesignLayer draws on the map and what designApi
// writes into the KMZ, so operators can read any output at a glance.

const SHAPE_PATH = {
  diamond: 'M 12,2 L 22,12 L 12,22 L 2,12 Z',
  house: 'M 4,13 L 4,21 L 20,21 L 20,13 L 12,3 Z',
  triangle: 'M 12,3 L 21,21 L 3,21 Z',
  square: 'M 4,4 L 20,4 L 20,20 L 4,20 Z',
  hexagon: 'M 12,3 L 20,7.5 L 20,16.5 L 12,21 L 4,16.5 L 4,7.5 Z',
  circle: 'M 12,3 C 16.97,3 21,7.03 21,12 C 21,16.97 16.97,21 12,21 C 7.03,21 3,16.97 3,12 C 3,7.03 7.03,3 12,3 Z',
};

const fa = (n) => Number(n).toLocaleString('fa-IR');
// '8x5' -> '۸/۵', '14x10' -> '۱۴/۱۰'
const ductFa = (t) => String(t).split('x').map(fa).join('/');

// The capacity wording follows the shown design's capacity_model (round 23: splitters
// and patch panel are chosen per project); with no design it reads the defaults.
function points(cm) {
  const odc = cm.odc_splitter;
  const fat = cm.fat_splitter;
  const m = cm.mfat_splitter;
  const newModel = cm.max_cables_per_microduct != null;
  // A panel's usable ODC ports: a third of its cores, floored to a multiple of 4.
  const panelPorts = Math.floor(cm.patch_panel_cores / 12) * 4;
  return [
    { label: 'مرکز (OLT)', desc: 'محل تجهیزات مرکزی - مبدأ شبکه. وقتی اپراتور سایت وارد کرده، همهٔ OLTها در سایت جای می‌گیرند و هیچ‌کدام در ODC نمی‌رود: OLT اضافه (پس از ارتقای کارت‌ها) در نزدیک‌ترین سایت اپراتور به کابینتِ نیازمند ظرفیت نصب می‌شود. اگر سایتی وارد نشود، OLT درون مرکزی‌ترین ODC (وقتی سه سایت متمایز یا بیشتر است، روی مسیر رینگ و نه ته کوچهٔ بن‌بست)، یا در یک سایت وقتی همهٔ کابینت‌هایش MFAT است. سایت واردشده مرکز مخابراتی موجود است و هزینه‌ی کابینت ندارد؛ فقط سایت تازه‌ای که موتور بدون مرکز موجود و میزبان ODC می‌سازد روی ردیف کابینت ODC حساب می‌شود. ۱۶، ۳۲، ۴۸ یا ۶۴ پورت PON؛ پورت آزاد مجاز است', shape: 'diamond', color: '#16a34a', kmz: 'سنجاق سبز (grn-pushpin)' },
    { label: 'کابینت (ODC)', desc: `کابینت توزیع نوری حداقل ۴ پورته و همیشه مضربی از ۴ (هر پورت یک اسپلیتر ۱×${fa(odc)} به ${fa(odc)} FAT)، تا سقف پچ‌پنلش (یک‌سومِ کرها: ${fa(cm.patch_panel_cores)} کر = ${fa(panelPorts)} پورت${!newModel && cm.patch_panel_cores === 128 ? ' + ۲ پورت برای MFAT' : ''}). برای ظرفیت بیشتر اول کارت‌های OLT ارتقا می‌یابد (۱۶ ← ۳۲ ← ۴۸ ← ۶۴) و OLT دوم آخرین گزینه است${newModel && !cm.mfat_enable ? '؛ در حالت «تمام ODC» محلهٔ کوچک یک ODC کم‌پُر می‌گیرد و فقط پورت‌های لازمش اسپلیتر دارد' : ''}`, shape: 'house', color: '#06b6d4', kmz: 'خانه‌ی فیروزه‌ای (homegardenbusiness)' },
    { label: 'MFAT (توزیع کوچک)', desc: m != null ? `نقطه توزیع کوچک، هم‌ردهٔ ODC و مستقیم از پورت OLT، دوپورته یا چهارپورته. روی همهٔ پورت‌ها اسپلیتر ۱:${fa(m)} دارد: پورت ۱ برای ${fa(m)} مشترک مستقیم و بقیهٔ پورت‌ها ${fa(m)} یا ${fa(3 * m)} FAT (${fa(m + m * fat)} یا ${fa(m + 3 * m * fat)} مشترک). تعدادش را فقط بودجهٔ پورت OLT محدود می‌کند؛ خط تغذیه‌اش (۴ کر) یک کابل با اندازهٔ کابل ODC پروژه است و در نقشه با رنگ FAT رسم می‌شود` : `نقطه توزیع کوچک، هم‌ردهٔ ODC و مستقیم از پورت OLT، دوپورته یا چهارپورته. پورت ۱ خودش نقطهٔ سرویسِ ${fa(fat)} مشترک است (دراپ مستقیم) و بقیهٔ پورت‌ها ${fa(odc)} یا ${fa(3 * odc)} FAT می‌گیرند (${fa(fat * (1 + odc))} یا ${fa(fat * (1 + 3 * odc))} مشترک). تعدادش را فقط بودجهٔ پورت OLT محدود می‌کند؛ خط تغذیه‌اش (۴ کر) یک کابل با اندازهٔ کابل ODC پروژه است و در نقشه با رنگ FAT رسم می‌شود`, shape: 'triangle', color: '#ec4899', kmz: 'مثلث صورتی (triangle)' },
    { label: `FAT ⁧(${fa(fat)} مشترک)⁩`, desc: `جعبهٔ FAT روی نمای ساختمان: فقط یک اسپلیتر ۱:${fa(fat)} برای ${fa(fat)} مشترک؛ چند FAT کنار هم روی یک ساختمانِ پرمشترک مجاز است. کابل لایهٔ FAT ⁧${fa(cm.fat_cable_cores)}⁩ کر است و هر FAT ⁧۲⁩ کر می‌گیرد؛ اگر یک کابل کم بیاید، ${newModel ? 'کابل دوم در لوز (زیرداکت) دیگری از همان میکروداکت کشیده می‌شود؛ بیش از ۲ کابل در یک میکروداکت مجاز نیست' : 'کابل موازی در لوز (زیرداکت) دیگری از میکروداکت کشیده می‌شود'}`, shape: 'triangle', color: '#3b82f6', kmz: 'مثلث آبی (triangle)' },
    { label: 'حوضچه (HH)', desc: 'حوضچه در جلوی ODC و OLT و هاب‌های انشعاب؛ هرجا دو داکت هفت‌لوز به هم برسند یا تقاطع داشته باشند، حتی وسط قطعه و روی مترو، حوضچه الزامی است. برای شوت پیوستهٔ بیش از ۱۵۰۰ متر نیز حوضچهٔ میانی لازم است' + (newModel ? '؛ لایهٔ FAT چهارکاناله است و انشعاب‌هایش کانکتور T یا H می‌گیرند' : ''), shape: 'square', color: '#1e3a8a', kmz: 'مربع سرمه‌ای (square)' },
    { label: 'کانکتور T (TC)', desc: 'تکْ‌تپِ روی ترانشه (یک FAT)', shape: 'hexagon', color: '#f59e0b', kmz: 'دایره‌ی کهربایی' },
    { label: 'کانکتور H (HC)', desc: 'هابِ انشعابِ مشترک (≥۲ FAT روبه‌رو)', shape: 'circle', color: '#a21caf', kmz: 'دونات بنفش (donut)' },
  ];
}

// New wording is identified by the capacity-model key, not by duct_plan.
const lines = (cm, metered) => {
  const newModel = cm.max_cables_per_microduct != null;
  return [
    { label: 'مترو (Metro)', desc: newModel
      ? `فیدر اصلی: OLT ← ODC. کابل ${fa(cm.metro_cable_cores)} کر در میکروداکت هفت‌کاناله ⁧(${ductFa(cm.metro_microduct_type)})⁩؛ حداکثر ۲ کابل در هر میکروداکت`
      : `فیدر اصلی: OLT ← ODC. کابل ${fa(cm.metro_cable_cores)} کر در داکت جداگانهٔ رینگ`, color: '#ef4444', width: 6, kmz: 'قرمز، ضخامت ۵' },
    { label: 'لایهٔ ODC', desc: newModel
      ? `تغذیهٔ کابینت از رینگ یا سایت، کابل ${fa(cm.odc_cable_cores)} کر در میکروداکت هفت‌کاناله ⁧(${ductFa(cm.odc_microduct_type)})⁩. ۸/۵ فقط کابل ۴۸ کر و ۱۴/۱۰ کابل ۷۲ یا ۹۶ کر می‌گیرد؛ حداکثر ۲ کابل در هر میکروداکت`
      : `تغذیهٔ کابینت از رینگ یا سایت، کابل ${fa(cm.odc_cable_cores)} کر. میکروداکت لایه‌های ODC و FAT ⁧(${ductFa(cm.odc_microduct_type)})⁩ را اپراتور انتخاب می‌کند؛ ۸/۵ در هر لوز حداکثر کابل ۴۸ کر و ۱۴/۱۰ حداکثر کابل ۹۶ کر می‌گیرد؛ هر دو اندازه با ۴ یا ۷ لوز عرضه می‌شوند و طرح از ۷ لوز استفاده می‌کند`, color: '#eab308', width: 4, kmz: 'زرد، ضخامت ۳' },
    { label: 'لایهٔ FAT', desc: newModel
      ? `میکروداکت چهارکاناله ⁧(${ductFa(cm.fat_microduct_type)})⁩ - توزیع به FATها؛ هر کابل با همهٔ کرهایش در یک لوز و حداکثر ۲ کابل در هر میکروداکت`
      : 'میکروداکت هفت‌لوز - توزیع به FATها؛ هر کابل با همهٔ کرهایش در یک لوز شوت می‌شود و کابل موازی در لوزِ دیگر', color: '#8b5cf6', width: 3, kmz: 'بنفش، ضخامت ۳' },
    ...(!newModel || metered ? [{ label: 'دراپ (Drop)', desc: 'کابل سرویس از FAT یا MFAT تا ساختمان، برآوردشده از تعداد مشترک؛ در تولید خطوط دراپ رسم نمی‌شوند', color: '#a78bfa', width: 2, kmz: 'بنفشِ روشن' }] : []),
  ];
};

const AREAS = [
  { label: 'پوشش مرکز (OLT)', desc: 'محدوده‌ی سرویس مرکز', color: '#ef4444' },
  { label: 'پوشش کابینت (ODC)', desc: 'شعاع پوشش کابینت (پیش‌فرض ۵۰۰ متر)', color: '#eab308' },
];

function Swatch({ children, tint }) {
  return (
    <div style={{
      width: 40, height: 40, flexShrink: 0, borderRadius: 10,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `${tint}1f`, border: `1px solid ${tint}55`,
    }}>
      {children}
    </div>
  );
}

function ShapeSwatch({ shape, color }) {
  return (
    <Swatch tint={color}>
      <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
        <path d={SHAPE_PATH[shape]} fill={color} stroke="#0b1220" strokeWidth="1.2" />
      </svg>
    </Swatch>
  );
}

function LineSwatch({ color, width }) {
  return (
    <Swatch tint={color}>
      <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
        <line x1="3" y1="12" x2="21" y2="12" stroke={color} strokeWidth={width} strokeLinecap="round" />
      </svg>
    </Swatch>
  );
}

function CircleSwatch({ color }) {
  return (
    <Swatch tint={color}>
      <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="8.5" fill="none" stroke={color} strokeWidth="2.2" strokeDasharray="3 2.5" />
      </svg>
    </Swatch>
  );
}

function Row({ swatch, label, desc, kmz }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px',
      borderRadius: 12, background: 'rgba(30,41,59,0.5)',
      border: '1px solid rgba(148,163,184,0.12)',
    }}>
      {swatch}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#f1f5f9' }}>{label}</div>
        <div style={{ fontSize: 11.5, color: '#9aa7b8', lineHeight: 1.7, marginTop: 1 }}>{desc}</div>
        {kmz && (
          <div style={{ fontSize: 10.5, color: '#5eead4', marginTop: 3, opacity: 0.85 }}>
            در KMZ: {kmz}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9,
        fontWeight: 800, fontSize: 12.5, color: '#5eead4', letterSpacing: 0.2,
      }}>
        <span style={{ width: 5, height: 16, borderRadius: 3, background: 'linear-gradient(180deg,#2dd4bf,#0d9488)' }} />
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  );
}

// Symbology / KMZ legend modal. Close on backdrop click, the X button, or Esc.
export default function DesignHelpModal({ onClose, summary }) {
  const cm = capacityModel(summary);
  const titleId = useId();
  const close = useRef(null);
  const body = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const previousFocus = document.activeElement;
    close.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current();
      // Both tab directions cycle through the close button and keyboard-scrollable body.
      if (e.key === 'Tab') {
        e.preventDefault();
        (document.activeElement === close.current ? body : close).current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); previousFocus?.focus(); };
  }, []);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 50, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
        background: 'rgba(2,6,23,0.66)', backdropFilter: 'blur(4px)',
        fontFamily: "'Vazirmatn', Tahoma, sans-serif", direction: 'rtl',
      }}
    >
      <div
        role="dialog" aria-modal="true" aria-labelledby={titleId} className="design-help"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(470px, 94vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
          background: 'linear-gradient(160deg, #111c30 0%, #15233b 100%)',
          border: '1px solid rgba(148,163,184,0.22)', borderRadius: 18,
          boxShadow: '0 28px 70px rgba(2,6,23,0.72), 0 1px 0 rgba(255,255,255,0.05) inset',
          color: '#e2e8f0', overflow: 'hidden',
        }}
      >
        <style>{`.design-help ::selection { background: #115e59; color: #f0fdfa; }
          .design-help button:focus-visible, .design-help [tabindex]:focus-visible { outline: 2px solid #2dd4bf; outline-offset: 3px; }
          .design-help [tabindex]:focus-visible { outline-offset: -2px; }
          .design-help { scrollbar-color: #64748b #15233b; }`}</style>
        {/* header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 12, padding: '18px 22px 14px',
          borderBottom: '1px solid rgba(148,163,184,0.16)',
          background: 'linear-gradient(180deg, rgba(45,212,191,0.08), rgba(45,212,191,0))',
        }}>
          <div>
            <h2 id={titleId} style={{ margin: 0, fontWeight: 800, fontSize: 16, letterSpacing: 0.2 }}>راهنمای نمادها و رنگ‌ها</h2>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4, lineHeight: 1.7 }}>
              نمادها و رنگ‌ها برای همهٔ اپراتورها یکسان است (برگرفته از KMZ مرجع)؛ چه روی نقشه، چه در فایل KMZ.
            </div>
          </div>
          <button
            ref={close} type="button" onClick={onClose} aria-label="بستن"
            style={{
              width: 32, height: 32, flexShrink: 0, borderRadius: 9, cursor: 'pointer',
              background: 'rgba(51,65,85,0.7)', color: '#cbd5e1',
              border: '1px solid rgba(148,163,184,0.3)', fontSize: 17, lineHeight: 1,
            }}
          ><X size={17} aria-hidden="true" /></button>
        </div>

        {/* scrollable body */}
        <div ref={body} tabIndex={0} role="region" aria-label="فهرست نمادها"
          style={{ padding: '6px 22px 22px', overflowY: 'auto', borderRadius: '0 0 17px 17px' }}>
          <Section title="نقاط (تجهیزات)">
            {points(cm).map((p) => (
              <Row key={p.label} swatch={<ShapeSwatch shape={p.shape} color={p.color} />}
                   label={p.label} desc={p.desc} kmz={p.kmz} />
            ))}
          </Section>

          <Section title="خطوط (کابل‌ها)">
            {lines(cm, dropsMetered(summary)).map((l) => (
              <Row key={l.label} swatch={<LineSwatch color={l.color} width={l.width} />}
                   label={l.label} desc={l.desc} kmz={l.kmz} />
            ))}
          </Section>

          {cm.max_cables_per_microduct != null && !dropsMetered(summary) && <p style={{ fontSize: 11.5, color: '#cbd5e1', lineHeight: 1.8 }}>
            کابل سرویس از FAT یا MFAT تا ساختمان؛ فعلاً در طرح و برآورد حساب نمی‌شود.
          </p>}
          <Section title="دایره‌های پوشش (POP)">
            {AREAS.map((a) => (
              <Row key={a.label} swatch={<CircleSwatch color={a.color} />}
                   label={a.label} desc={a.desc} />
            ))}
          </Section>

          <div style={{
            display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 18, padding: '11px 13px',
            borderRadius: 12, background: 'rgba(13,148,136,0.1)', border: '1px solid rgba(45,212,191,0.22)',
            fontSize: 11.5, color: '#cbd5e1', lineHeight: 1.8,
          }}>
            <Lightbulb size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 3 }} />
            <span>کابل‌ها فقط از مسیرِ خیابان‌ها و کوچه‌ها عبور می‌کنند و سرِ تقاطع شاخه می‌شوند؛ هیچ خطی از روی ساختمان رد نمی‌شود.</span>
          </div>
          <div style={{ marginTop: 12, padding: '11px 13px', borderRadius: 12,
            background: 'rgba(13,148,136,0.1)', border: '1px solid rgba(45,212,191,0.22)',
            fontSize: 11.5, color: '#cbd5e1', lineHeight: 1.8 }}>
            پروفایل اپراتور مقدارهای اسپلیتر، پچ‌پنل، کابل و میکروداکت را برای هر طرح پر می‌کند؛ مدیر سامانه پروفایل‌ها را در بخش «طراحی» پنل مدیریت می‌سازد.
          </div>
        </div>
      </div>
    </div>
  );
}
